import React, { type MutableRefObject, useCallback } from 'react';

import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import BrowserPaymentButton from '@/components/payments/components/browserPaymentButton';
import { type BrowserPaymentRequestType } from '@/components/payments/hooks/useBrowserPayment';
import type { CHECKOUT_CONTEXT } from '@/consts/checkout';
import { PAYMENT_TYPES, PROCESSOR_NAMES } from '@/consts/payments';
import { PaymentMethodService } from '@/services/paymentsService/client';
import { type NewPaymentMethod } from '@/services/resubscriptionService/types';
import type { RegisterPaymentMethodData } from '@/types/payments/types';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';

interface ApplePayButtonStyles
  extends stripe.elements.PaymentRequestButtonStyleOptions {
  borderRadius: string;
}

/**
 * The button links the user's Apple Pay account and registers the payment in the backend.
 *
 * @prop paymentRequest The payment request object for Apple Pay payments.
 * @prop onSuccess Executed on the successfully registered payment method.
 * @prop onError Executed on any error message in the workflow.
 * @prop buttonStyleOptions Optional. Overrides any default options in usePaypalButton.
 * @prop checkoutContext Optional. Indicates context if rendering in a checkout flow.
 */
const RegisterApplePayPaymentMethodButton: React.FC<{
  paymentRequest: MutableRefObject<BrowserPaymentRequestType>;
  onSuccess: (paymentMethod: NewPaymentMethod) => void;
  onError: (message: string) => void;
  buttonStyleOptions?: Partial<ApplePayButtonStyles>;
  checkoutContext?: valueof<typeof CHECKOUT_CONTEXT>;
}> = ({
  paymentRequest,
  onSuccess,
  onError,
  buttonStyleOptions,
  checkoutContext,
}) => {
  const { brand } = useFlowContext();
  const getApplePayPaymentMethod = (token: stripe.Token): NewPaymentMethod => {
    return {
      processor: PROCESSOR_NAMES.STRIPE,
      type: PAYMENT_TYPES.APPLE_PAY,
      cardBrand: token.card?.brand,
      cardLastFour: token.card?.last4,
      cardFundingType: token.card?.funding,
    };
  };

  const formatNewPaymentMethodToData = (
    newPaymentMethod: NewPaymentMethod,
    paymentNonce: string,
  ): RegisterPaymentMethodData => {
    return {
      method: newPaymentMethod.type,
      paymentNonce,
      vendor: newPaymentMethod.processor,
      ...(checkoutContext ? { checkoutContext } : {}),
    };
  };

  const onClick = async () => {
    trackEvent(AnalyticsEvent.APPLE_PAY_CLICKED);
  };

  const onStorePaymentSuccess = (newPaymentMethod: NewPaymentMethod) => {
    trackEvent(AnalyticsEvent.APPLE_PAY_SUCCEEDED);
    onSuccess(newPaymentMethod);
  };

  const onStorePaymentError = (message: string) => {
    trackEvent(AnalyticsEvent.APPLE_PAY_FAILED);
    onError(message);
  };

  const storeApplePayPayment = useCallback(
    async (token: stripe.paymentRequest.StripeTokenPaymentResponse) => {
      const newPaymentMethod = getApplePayPaymentMethod(token.token);

      await PaymentMethodService.storePaymentMethod({
        brand,
        data: formatNewPaymentMethodToData(newPaymentMethod, token.token.id),
        onSuccess: () => onStorePaymentSuccess(newPaymentMethod),
        onError: onStorePaymentError,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brand, checkoutContext, onSuccess, onError],
  );

  return (
    <BrowserPaymentButton
      paymentRequest={paymentRequest}
      onSuccess={storeApplePayPayment}
      onClick={onClick}
      buttonStyleOptions={buttonStyleOptions}
    />
  );
};

export default RegisterApplePayPaymentMethodButton;
